import { Action, Reducer } from "redux";
import { AppThunkAction, ApplicationState } from "./";
import { get, post, remove, GetOptions, RequestOptions } from "../fetch-interceptor";
import { catchApiError, defaultCatch, handledError } from "./utils";
import {
    KnownAction as NotificationsKnownAction,
    actionCreators as NotificationsActionCreators,
    NotificationType
} from "./NotificationsStore";
import { toDate } from "../components/utils/common";
import { IsTimesheetApprovalsEnabled } from "../components/timeTracking/common";
import { PersonInfo } from "../components/common/inputs/PersonPickerInput";
import { Dictionary } from "../entities/common";
import { TenantState } from "./Tenant";

const HTTP_FORBIDDEN = 403;
const ON_BEHALF_OF_HEADER = "X-PPMX-On-Behalf-Of";

// State definition
export enum DelegationScope {
    TimesheetApproval = 0
}

export enum DelegationStatus {
    NotActive = 0,
    Pending = 1,
    Active = 2,
    Expired = 3
}

export interface DelegationSession {
    candidates: PersonInfo[];
    actingAs?: PersonInfo;
}

export interface MyDelegation {
    scope: DelegationScope;
    delegateUsers: PersonInfo[];
    from?: Date;
    until?: Date;
}

export interface DelegationUpdate {
    scope: DelegationScope;
    delegateUserIds: string[];
    from?: string;
    until?: string;
}

export interface DelegationState {
    sessions: Dictionary<DelegationSession>;
    myDelegations: Dictionary<MyDelegation>;
}

// Action types
export enum ActionTypes {
    RECEIVE_DELEGATION_SESSION = "RECEIVE_DELEGATION_SESSION",
    START_DELEGATION_MODE = "START_DELEGATION_MODE",
    STOP_DELEGATION_MODE = "STOP_DELEGATION_MODE",
    RECEIVE_MY_DELEGATION = "RECEIVE_MY_DELEGATION"
}

// Action interfaces
interface ReceiveDelegationSessionAction {
    type: ActionTypes.RECEIVE_DELEGATION_SESSION;
    scope: DelegationScope;
    session: DelegationSession;
}

interface StartDelegationModeAction {
    type: ActionTypes.START_DELEGATION_MODE;
    scope: DelegationScope;
    actingAs: PersonInfo;
}

interface StopDelegationModeAction {
    type: ActionTypes.STOP_DELEGATION_MODE;
    scope: DelegationScope;
}

interface ReceiveMyDelegationAction {
    type: ActionTypes.RECEIVE_MY_DELEGATION;
    scope: DelegationScope;
    delegation: MyDelegation;
}

export type KnownAction =
    | ReceiveDelegationSessionAction
    | StartDelegationModeAction
    | StopDelegationModeAction
    | ReceiveMyDelegationAction;

// Action creators
export const actionCreators = {
    loadSession: (scope: DelegationScope): AppThunkAction<KnownAction> => dispatch => {
        get<DelegationSession>("api/user/delegation/session", { scope })
            .then(session => dispatch({ type: ActionTypes.RECEIVE_DELEGATION_SESSION, scope, session }))
            .catch(defaultCatch(dispatch));
    },

    startDelegationMode: (scope: DelegationScope, actingAs: PersonInfo): AppThunkAction<KnownAction | NotificationsKnownAction> =>
        dispatch => {
            dispatch({ type: ActionTypes.START_DELEGATION_MODE, scope, actingAs });
            post<DelegationSession>("api/user/delegation/session", { scope, delegatorId: actingAs.id })
                .then(session => dispatch({ type: ActionTypes.RECEIVE_DELEGATION_SESSION, scope, session }))
                .catch(catchApiError(_ => {
                    dispatch({ type: ActionTypes.STOP_DELEGATION_MODE, scope });
                    dispatch(NotificationsActionCreators.pushNotification({
                        message: `Failed to start delegation mode. ${_}`,
                        type: NotificationType.Error
                    }));
                }));
        },

    stopDelegationMode: (scope: DelegationScope): AppThunkAction<KnownAction | NotificationsKnownAction> =>
        dispatch => {
            dispatch({ type: ActionTypes.STOP_DELEGATION_MODE, scope });
            remove<DelegationSession>(`api/user/delegation/session?scope=${scope}`)
                .then(session => dispatch({ type: ActionTypes.RECEIVE_DELEGATION_SESSION, scope, session }))
                .catch(catchApiError(_ => {
                    dispatch(NotificationsActionCreators.pushNotification({
                        message: `Failed to stop delegation mode. ${_}`,
                        type: NotificationType.Error
                    }));
                }));
        },

    loadMyDelegation: (scope: DelegationScope): AppThunkAction<KnownAction> => dispatch => {
        get<MyDelegation>("api/user/delegation", { scope })
            .then(delegation => dispatch({ type: ActionTypes.RECEIVE_MY_DELEGATION, scope, delegation }))
            .catch(defaultCatch(dispatch));
    },

    updateMyDelegation: (update: DelegationUpdate): AppThunkAction<KnownAction | NotificationsKnownAction> => dispatch => {
        post<MyDelegation>("api/user/delegation", update)
            .then(delegation => dispatch({ type: ActionTypes.RECEIVE_MY_DELEGATION, scope: update.scope, delegation }))
            .catch(catchApiError(_ => {
                dispatch(NotificationsActionCreators.pushNotification({
                    message: `Failed to save delegation settings. ${_}`,
                    type: NotificationType.Error
                }));
            }));
    }
};

export type DelegatedApi = {
    actingAs?: PersonInfo;
    get: <T>(url: string, data?: any, options?: GetOptions) => Promise<T>;
    post: <T>(url: string, data: object, options?: RequestOptions) => Promise<T>;
};

// get/post that add the on-behalf-of header while delegation mode is on.
// A 403 stops delegation mode and notifies the user here, so callers don't have to handle it.
export const onBehalfOf = (
    scope: DelegationScope,
    dispatch: (action: any) => void,
    getState: () => ApplicationState
): DelegatedApi => {
    const actingAs = getActingAs(getState(), scope);

    if (!actingAs) {
        return { get, post };
    }

    const header = { [ON_BEHALF_OF_HEADER]: actingAs.id };

    const onRejected = (error: any): never => {
        if (error?.response?.status !== HTTP_FORBIDDEN || !getActingAs(getState(), scope)) {
            throw error;
        }

        dispatch(actionCreators.stopDelegationMode(scope));
        dispatch(NotificationsActionCreators.pushNotification({
            message: "Delegation is no longer active. Showing your own data.",
            type: NotificationType.Warn
        }));

        throw handledError();
    };

    return {
        actingAs,
        get: <T>(url: string, data?: any, options?: GetOptions) =>
            get<T>(url, data, { ...options, headers: { ...options?.headers, ...header } }).catch(onRejected),
        post: <T>(url: string, data: object, options?: RequestOptions) =>
            post<T>(url, data, { ...options, headers: { ...options?.headers, ...header } }).catch(onRejected)
    };
};

export const getActingAs = (state: ApplicationState, scope: DelegationScope): PersonInfo | undefined =>
    state.delegation.sessions[scope]?.actingAs;

export const getCandidates = (state: ApplicationState, scope: DelegationScope): PersonInfo[] =>
    state.delegation.sessions[scope]?.candidates ?? [];

export const isDelegationEnabled = (tenant: TenantState, scope: DelegationScope): boolean =>
    scope === DelegationScope.TimesheetApproval
        ? IsTimesheetApprovalsEnabled(tenant) && tenant.timeTracking.globalSettings.delegationModeEnabled
        : false;

export const getDelegationStatus = (delegation: MyDelegation | undefined, tenantToday: Date): DelegationStatus => {
    if (!delegation?.delegateUsers.length || !delegation.from || !delegation.until) {
        return DelegationStatus.NotActive;
    }

    return tenantToday < delegation.from
        ? DelegationStatus.Pending
        : tenantToday > delegation.until
            ? DelegationStatus.Expired
            : DelegationStatus.Active;
};

const initialState: DelegationState = {
    sessions: {},
    myDelegations: {}
};

const _checkDates = (delegation: MyDelegation): MyDelegation => ({
    ...delegation,
    from: toDate(delegation.from),
    until: toDate(delegation.until)
});

const _withSession = (state: DelegationState, scope: DelegationScope, session: DelegationSession): DelegationState => ({
    ...state,
    sessions: { ...state.sessions, [scope]: session }
});

export const reducer: Reducer<DelegationState> = (state: DelegationState = initialState, incomingAction: Action) => {
    const action = incomingAction as KnownAction;

    switch (action.type) {
        case ActionTypes.RECEIVE_DELEGATION_SESSION:
            return _withSession(state, action.scope, action.session);

        case ActionTypes.START_DELEGATION_MODE:
            return _withSession(state, action.scope, {
                candidates: getSessionCandidates(state, action.scope),
                actingAs: action.actingAs
            });

        case ActionTypes.STOP_DELEGATION_MODE:
            return _withSession(state, action.scope, {
                candidates: getSessionCandidates(state, action.scope),
                actingAs: undefined
            });

        case ActionTypes.RECEIVE_MY_DELEGATION:
            return {
                ...state,
                myDelegations: { ...state.myDelegations, [action.scope]: _checkDates(action.delegation) }
            };

        default:
            const exhaustiveCheck: never = action;
    }

    return state;
};

const getSessionCandidates = (state: DelegationState, scope: DelegationScope) => state.sessions[scope]?.candidates ?? [];
